import { useState, useEffect } from 'react';
import {
  Box,
  Typography,
  Paper,
  Grid,
  TextField,
  InputAdornment,
  Alert,
  Snackbar,
  CircularProgress,
  IconButton,
  Button,
  Tooltip,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
} from '@mui/material';
import { Search, ShoppingCart, Plus, Minus, Printer, Bluetooth, BluetoothOff, Check } from 'lucide-react';
import { useLanguage } from '../../contexts/LanguageContext';
import { useAuth } from '../../contexts/AuthContext';
import { Cart } from '../../components/Cart';
import { useSales } from '../../hooks/useSales';
import { CreateSaleDto } from '../../services/salesService';
import { api, Product } from '../../services/api';
import {
  isBluetoothAvailable,
  isPrinterConnected,
  connectPrinter,
  disconnectPrinter,
  printReceipt,
  getPrinterName,
  PrintReceiptData,
  PrintCartItem,
} from '../../services/thermalPrinterService';

interface CartItem {
  product: Product;
  quantity: number;
}

export default function Sales() {
  const { t } = useLanguage();
  const { user } = useAuth();
  const { createSale } = useSales();
  const [products, setProducts] = useState<Product[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [searchTerm, setSearchTerm] = useState('');
  const [cartItems, setCartItems] = useState<CartItem[]>([]);
  const [isProcessing, setIsProcessing] = useState(false);
  const [successMessage, setSuccessMessage] = useState('');
  const [printerConnected, setPrinterConnected] = useState(false);
  const [printerName, setPrinterName] = useState<string | null>(null);
  const [connectingPrinter, setConnectingPrinter] = useState(false);
  const [showPrintDialog, setShowPrintDialog] = useState(false);
  const [lastSaleItems, setLastSaleItems] = useState<CartItem[]>([]);
  const [lastSaleTotal, setLastSaleTotal] = useState(0);
  const [isPrinting, setIsPrinting] = useState(false);

  useEffect(() => {
    if (user?.companyId) {
      fetchProducts();
    }
  }, [user]);

  const fetchProducts = async () => {
    setLoading(true);
    try {
      const fetchedProducts = await api.getProductsByCompany(user!.companyId);
      setProducts(fetchedProducts || []);
      console.log('Fetched products:', fetchedProducts); // Debug log
    } catch (error) {
      console.error('Error fetching products:', error);
      setError(error instanceof Error ? error.message : 'Failed to fetch products');
      setProducts([]);
    } finally {
      setLoading(false);
    }
  };

  const filteredProducts = products?.filter(product => 
    product.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
    product.code.toLowerCase().includes(searchTerm.toLowerCase())
  ) || [];

  const handleAddToCart = (product: Product) => {
    if (product.quantity <= 0) {
      setError("Ce produit n'est plus en stock");
      return;
    }

    setCartItems(prev => {
      const existingItem = prev.find(item => item.product._id === product._id);
      if (existingItem) {
        // Vérifier si on peut augmenter la quantité
        if (existingItem.quantity >= product.quantity) {
          setError("Quantité maximum atteinte pour ce produit");
          return prev;
        }
        return prev.map(item =>
          item.product._id === product._id
            ? { ...item, quantity: item.quantity + 1 }
            : item
        );
      }
      return [...prev, { product, quantity: 1 }];
    });
  };

  const handleUpdateQuantity = (productId: string, newQuantity: number) => {
    setCartItems(prev =>
      prev.map(item => {
        if (item.product._id === productId) {
          // Vérifier si la nouvelle quantité est valide
          if (newQuantity <= 0) {
            return item; // Ignorer les quantités négatives
          }
          if (newQuantity > item.product.quantity) {
            setError("Quantité demandée supérieure au stock disponible");
            return item;
          }
          return { ...item, quantity: newQuantity };
        }
        return item;
      })
    );
  };

  const handleRemoveItem = (productId: string) => {
    setCartItems(prev => prev.filter(item => item.product._id !== productId));
  };

  const handleCheckout = async () => {
    if (cartItems.length === 0) return;

    setIsProcessing(true);
    try {
      const total = cartItems.reduce((sum, item) => sum + (item.product.salePrice * item.quantity), 0);
      
      // Récupérer l'utilisateur depuis le localStorage
      const userStr = localStorage.getItem('user');
      if (!userStr) {
        throw new Error('Utilisateur non trouvé');
      }
      
      const userData = JSON.parse(userStr);
      if (!userData._id) {
        throw new Error('ID utilisateur non trouvé');
      }
      
      const saleData: CreateSaleDto = {
        companyId: user!.companyId,
        products: cartItems.map(item => ({
          productId: item.product._id,
          quantity: item.quantity,
          price: item.product.salePrice
        })),
        totalAmount: total,
        userId: userData._id,
        paymentMode: "Cash",
        status: "completed",
        discount: 0
      };

      console.log('Sending sale data:', saleData);
      await createSale(saleData);
      
      // Sauvegarder les détails pour l'impression avant de vider le panier
      setLastSaleItems([...cartItems]);
      setLastSaleTotal(total);
      
      setSuccessMessage('Vente enregistrée avec succès');
      setCartItems([]);
      fetchProducts(); // Refresh products to update quantities
      
      // Afficher le dialogue d'impression
      setShowPrintDialog(true);
    } catch (error) {
      console.error('Error processing sale:', error);
      setError(error instanceof Error ? error.message : 'Failed to process sale');
    } finally {
      setIsProcessing(false);
    }
  };

  // ---- Fonctions d'impression thermique ----
  const handleConnectPrinter = async () => {
    setConnectingPrinter(true);
    try {
      await connectPrinter();
      setPrinterConnected(true);
      setPrinterName(getPrinterName());
      setSuccessMessage(`Imprimante "${getPrinterName() || 'Bluetooth'}" connectée`);
    } catch (error) {
      console.error('Erreur connexion imprimante:', error);
      setError(error instanceof Error ? error.message : 'Erreur de connexion Bluetooth');
      setPrinterConnected(false);
    } finally {
      setConnectingPrinter(false);
    }
  };

  const handleDisconnectPrinter = () => {
    disconnectPrinter();
    setPrinterConnected(false);
    setPrinterName(null);
  };

  const handlePrintReceipt = async () => {
    if (lastSaleItems.length === 0) return;

    setIsPrinting(true);
    try {
      const printItems: PrintCartItem[] = lastSaleItems.map(item => ({
        productName: item.product.name,
        quantity: item.quantity,
        unitPrice: item.product.salePrice,
        total: item.product.salePrice * item.quantity,
      }));

      const receiptData: PrintReceiptData = {
        companyName: user?.company?.name || 'Mon Entreprise',
        items: printItems,
        totalAmount: lastSaleTotal,
        paymentMode: 'Cash',
        vendorName: user?.username,
        date: new Date(),
      };

      await printReceipt(receiptData);
      setPrinterConnected(isPrinterConnected());
      setSuccessMessage('Reçu imprimé avec succès !');
      setShowPrintDialog(false);
    } catch (error) {
      console.error('Erreur impression:', error);
      setError(error instanceof Error ? error.message : 'Erreur lors de l\'impression');
    } finally {
      setIsPrinting(false);
    }
  };

  return (
    <Box sx={{ p: 3, bgcolor: '#f5f5f5', minHeight: '100vh' }}>
      <Box sx={{ mb: 4, display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start' }}>
        <Box>
          <Typography variant="h4" component="h1" gutterBottom>
            {t.sales}
          </Typography>
          <Typography color="text.secondary">
            {t.salesDescription}
          </Typography>
        </Box>

        {/* Bouton connexion imprimante Bluetooth */}
        {isBluetoothAvailable() && (
          <Tooltip title={printerConnected ? `Imprimante: ${printerName || 'Connectée'}` : 'Connecter une imprimante Bluetooth'}>
            <Button
              variant={printerConnected ? 'contained' : 'outlined'}
              startIcon={connectingPrinter ? <CircularProgress size={18} color="inherit" /> : printerConnected ? <Bluetooth size={18} /> : <BluetoothOff size={18} />}
              onClick={printerConnected ? handleDisconnectPrinter : handleConnectPrinter}
              disabled={connectingPrinter}
              size="small"
              sx={{
                bgcolor: printerConnected ? '#16a34a' : 'transparent',
                color: printerConnected ? 'white' : '#0C4A6E',
                borderColor: '#0C4A6E',
                '&:hover': {
                  bgcolor: printerConnected ? '#dc2626' : 'rgba(12,74,110,0.08)',
                  borderColor: printerConnected ? '#dc2626' : '#0C4A6E',
                },
                minWidth: 'auto',
                whiteSpace: 'nowrap',
              }}
            >
              {connectingPrinter ? 'Connexion...' : printerConnected ? printerName || 'Connectée' : 'Imprimante'}
            </Button>
          </Tooltip>
        )}
      </Box>

        <Grid container spacing={3}>
          {/* Product List */}
          <Grid item xs={12} md={8}>
            <Paper sx={{ p: 3, height: '100%' }}>
              <Box sx={{ mb: 3 }}>
                <TextField
                  fullWidth
                  size="small"
                  value={searchTerm}
                  onChange={(e) => setSearchTerm(e.target.value)}
                  placeholder={t.searchProducts}
                  InputProps={{
                    startAdornment: (
                      <InputAdornment position="start">
                        <Search size={20} />
                      </InputAdornment>
                    ),
                  }}
                />
              </Box>

              {loading ? (
                <Box sx={{ display: 'flex', justifyContent: 'center', alignItems: 'center', minHeight: 400 }}>
                  <CircularProgress />
                </Box>
              ) : products.length === 0 ? (
                <Box sx={{ textAlign: 'center', py: 4 }}>
                  <Typography color="text.secondary">
                    {t.noProducts}
                  </Typography>
                </Box>
              ) : (
                <Box sx={{ 
                  display: 'grid', 
                  gridTemplateColumns: 'repeat(auto-fill, minmax(200px, 1fr))', 
                  gap: 2,
                  maxHeight: 'calc(100vh - 300px)',
                  overflowY: 'auto',
                  p: 1
                }}>
                  {filteredProducts.map(product => (
                    <Paper
                      key={product._id}
                      elevation={2}
                      sx={{
                        p: 2,
                        transition: 'all 0.2s',
                        '&:hover': {
                          transform: 'translateY(-2px)',
                          boxShadow: 3
                        }
                      }}
                    >
                      <Typography variant="h6" noWrap sx={{ mb: 1 }}>
                        {product.name}
                      </Typography>
                      <Typography color="text.secondary" variant="body2" sx={{ mb: 1 }}>
                        {product.code}
                      </Typography>
                      <Typography variant="h6" color="primary" sx={{ mb: 1 }}>
                        {product.salePrice} FC
                      </Typography>
                      <Typography 
                        color={product.quantity > 0 ? 'success.main' : 'error.main'} 
                        variant="body2"
                        sx={{ mb: 2 }}
                      >
                        {product.quantity > 0 ? `${t.inStock}: ${product.quantity}` : t.outOfStock}
                      </Typography>
                      
                      {/* Cart Actions */}
                      <Box sx={{ display: 'flex', justifyContent: 'space-between', alignItems: 'center' }}>
                        {cartItems.find(item => item.product._id === product._id) ? (
                          <Box sx={{ display: 'flex', alignItems: 'center', gap: 1 }}>
                            <IconButton 
                              size="small"
                              onClick={() => {
                                const currentItem = cartItems.find(item => item.product._id === product._id);
                                if (currentItem) {
                                  handleUpdateQuantity(product._id, currentItem.quantity - 1);
                                }
                              }}
                            >
                              <Minus size={16} />
                            </IconButton>
                            <Typography>
                              {cartItems.find(item => item.product._id === product._id)?.quantity || 0}
                            </Typography>
                            <IconButton 
                              size="small"
                              onClick={() => handleAddToCart(product)}
                              disabled={product.quantity <= 0}
                            >
                              <Plus size={16} />
                            </IconButton>
                          </Box>
                        ) : (
                          <Button
                            variant="contained"
                            startIcon={<ShoppingCart size={16} />}
                            onClick={() => handleAddToCart(product)}
                            disabled={product.quantity <= 0}
                            size="small"
                            fullWidth
                            sx={{
                              bgcolor: '#0C4A6E',
                              '&:hover': {
                                bgcolor: '#0D5B86'
                              }
                            }}
                          >
                            Ajouter
                          </Button>
                        )}
                      </Box>
                    </Paper>
                  ))}
                </Box>
              )}
            </Paper>
          </Grid>

          {/* Cart */}
          <Grid item xs={12} md={4}>
            <Cart
              items={cartItems}
              onUpdateQuantity={handleUpdateQuantity}
              onRemoveItem={handleRemoveItem}
              onCheckout={handleCheckout}
              isProcessing={isProcessing}
            />
          </Grid>
        </Grid>

        <Snackbar
          open={!!error || !!successMessage}
          autoHideDuration={6000}
          onClose={() => {
            setError(null);
            setSuccessMessage('');
          }}
        >
          <Alert
            severity={error ? 'error' : 'success'}
            variant="filled"
            onClose={() => {
              setError(null);
              setSuccessMessage('');
            }}
          >
            {error || successMessage}
          </Alert>
        </Snackbar>

        {/* Dialog d'impression après une vente réussie */}
        <Dialog
          open={showPrintDialog}
          onClose={() => setShowPrintDialog(false)}
          maxWidth="xs"
          fullWidth
        >
          <DialogTitle sx={{ textAlign: 'center', pb: 1 }}>
            <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 1 }}>
              <Box sx={{
                width: 56,
                height: 56,
                borderRadius: '50%',
                bgcolor: '#dcfce7',
                display: 'flex',
                alignItems: 'center',
                justifyContent: 'center',
                mb: 1
              }}>
                <Check size={28} color="#16a34a" />
              </Box>
              <Typography variant="h6">Vente enregistrée !</Typography>
            </Box>
          </DialogTitle>
          <DialogContent>
            <Box sx={{ textAlign: 'center', mb: 2 }}>
              <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
                Voulez-vous imprimer le reçu ?
              </Typography>
              
              {/* Résumé de la vente */}
              <Paper variant="outlined" sx={{ p: 2, mb: 2, bgcolor: '#f9fafb' }}>
                <Typography variant="body2" sx={{ mb: 1 }}>
                  {lastSaleItems.length} article{lastSaleItems.length > 1 ? 's' : ''}
                </Typography>
                <Typography variant="h6" fontWeight="bold">
                  Total: {lastSaleTotal.toLocaleString('fr-FR')} FC
                </Typography>
              </Paper>

              {/* Statut de l'imprimante */}
              {isBluetoothAvailable() ? (
                <Box sx={{ 
                  display: 'flex', 
                  alignItems: 'center', 
                  justifyContent: 'center',
                  gap: 1, 
                  mb: 1,
                  color: printerConnected ? '#16a34a' : '#9ca3af'
                }}>
                  {printerConnected ? <Bluetooth size={16} /> : <BluetoothOff size={16} />}
                  <Typography variant="caption">
                    {printerConnected 
                      ? `Imprimante: ${printerName || 'Connectée'}` 
                      : 'Aucune imprimante connectée (sera connectée automatiquement)'
                    }
                  </Typography>
                </Box>
              ) : (
                <Typography variant="caption" color="error">
                  Bluetooth non disponible sur ce navigateur. Utilisez Chrome ou Edge.
                </Typography>
              )}
            </Box>
          </DialogContent>
          <DialogActions sx={{ px: 3, pb: 3, flexDirection: 'column', gap: 1 }}>
            <Button
              variant="contained"
              fullWidth
              startIcon={isPrinting ? <CircularProgress size={18} color="inherit" /> : <Printer size={18} />}
              onClick={handlePrintReceipt}
              disabled={isPrinting || !isBluetoothAvailable()}
              sx={{
                bgcolor: '#0C4A6E',
                '&:hover': { bgcolor: '#0D5B86' },
              }}
            >
              {isPrinting ? 'Impression en cours...' : 'Imprimer le reçu'}
            </Button>
            <Button
              variant="text"
              fullWidth
              onClick={() => setShowPrintDialog(false)}
              disabled={isPrinting}
              sx={{ color: '#6b7280' }}
            >
              Passer
            </Button>
          </DialogActions>
        </Dialog>
    </Box>
  );
}
